import { BookOpen, FileText, CircleCheck, MessageCircle, Sparkles } from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';
import { useScrollProgress } from '../../../hooks/useScrollProgress';
import andy from '../../../assets/images/askAndy.png';

const STEPS = [
  {
    n: '1',
    title: 'Andy studies your material',
    body: 'Upload your notes, slides, or a textbook chapter. Andy reads every page so every answer is grounded in your exact course content — never generic.',
  },
  {
    n: '2',
    title: 'Ask in your own words',
    body: 'Type or speak your question. Ask for an explanation, an example, a comparison, or a quick quiz — Andy replies instantly in plain English.',
  },
  {
    n: '3',
    title: 'Keep going until it clicks',
    body: "Andy checks your understanding, rephrases when you're stuck, and adapts the depth until the concept genuinely makes sense.",
  },
];

const N = STEPS.length;

/* ---------- right-side visuals ---------- */

const ReadingVisual = () => {
  const rows = [
    { icon: FileText, title: 'Chapters understood', meta: '4 of 4' },
    { icon: Sparkles, title: 'Key concepts learned', meta: '47' },
    { icon: CircleCheck, title: 'Ready to help', meta: 'Done' },
  ];
  return (
    <div className="card-dark rounded-2xl p-6">
      <div className="mb-4 flex items-center gap-2 text-white">
        <BookOpen size={18} className="text-accent" />
        <span className="text-sm font-semibold">Andy is reading your notes…</span>
      </div>
      <div className="mb-4 rounded-xl border border-accent/20 bg-accent/[0.06] p-4 text-center">
        <p className="text-sm font-semibold text-white">Biology_Chapter4_Mitosis.pdf</p>
        <p className="mt-1 text-[0.75rem] text-white/45">32 pages · fully analysed</p>
      </div>
      <div className="flex flex-col gap-2.5">
        {rows.map((r) => (
          <div
            key={r.title}
            className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/12 text-accent">
              <r.icon size={15} />
            </span>
            <span className="text-[0.85rem] font-medium text-white">{r.title}</span>
            <span className="ml-auto text-[0.72rem] text-white/45">{r.meta}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AskVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center gap-2 border-b border-white/8 pb-3 text-white">
      <MessageCircle size={16} className="text-accent" />
      <span className="text-[0.7rem] font-semibold uppercase tracking-wide">Chat with Andy</span>
    </div>
    <div className="flex flex-col gap-3">
      <div className="ml-auto max-w-[15rem] rounded-2xl rounded-tr-sm bg-accent/15 px-3 py-2 text-[0.78rem] text-white">
        Explain photosynthesis like I&apos;m 10
      </div>
      <div className="flex items-start gap-2">
        <img src={andy} alt="Andy" className="mt-0.5 h-6 w-6 shrink-0 rounded-full object-cover ring-1 ring-accent/40" />
        <div className="max-w-[16rem] rounded-2xl rounded-tl-sm bg-white/6 px-3 py-2 text-[0.78rem] leading-relaxed text-white/80">
          Plants are tiny chefs 👩‍🍳 — they mix sunlight, water and air to cook their
          own food (sugar) and breathe out the oxygen we need. 🌱
        </div>
      </div>
    </div>
  </div>
);

const ClickVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center gap-2 text-white">
      <CircleCheck size={18} className="text-accent" />
      <span className="text-sm font-semibold">Quick understanding check</span>
    </div>
    <p className="mb-3 text-[0.82rem] font-medium text-white">
      Which phase comes right after metaphase?
    </p>
    <div className="flex flex-col gap-2">
      {[
        { t: 'Prophase', ok: false },
        { t: 'Anaphase', ok: true },
        { t: 'Interphase', ok: false },
      ].map((o) => (
        <div
          key={o.t}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[0.76rem] ${
            o.ok
              ? 'border-accent/50 bg-accent/10 text-white'
              : 'border-white/8 bg-white/[0.02] text-white/60'
          }`}
        >
          {o.ok && <CircleCheck size={14} className="text-accent" />}
          {o.t}
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-start gap-2">
      <img src={andy} alt="Andy" className="mt-0.5 h-6 w-6 shrink-0 rounded-full object-cover ring-1 ring-accent/40" />
      <div className="rounded-2xl rounded-tl-sm bg-white/6 px-3 py-2 text-[0.76rem] text-white/80">
        Nailed it! 🎉 You&apos;ve got mitosis down.
      </div>
    </div>
  </div>
);

const VISUALS = [ReadingVisual, AskVisual, ClickVisual];

const Header = () => (
  <div className="text-center">
    <span className="eyebrow">How It Works</span>
    <h2 className="font-heading mx-auto mt-5 max-w-3xl text-[2rem] font-bold leading-[1.1] sm:text-[2.5rem] lg:text-[2.9rem]">
      <span className="head-fade">A Tutor That </span>
      <span className="text-accent-grad">Actually Knows Your Material</span>
    </h2>
    <p className="mx-auto mt-4 max-w-xl text-[0.95rem] text-white/55">
      Andy learns from your notes first, then teaches you from them — the way a
      great tutor would.
    </p>
  </div>
);

const StepRow = ({ step, isActive, fill, onClick }) => (
  <div className="flex gap-4" style={{ opacity: isActive ? 1 : 0.55, transition: 'opacity 0.4s ease' }}>
    <div className="relative mt-1 w-[3px] shrink-0 rounded-full bg-white/10">
      <span
        className="absolute left-0 top-0 w-full rounded-full bg-accent"
        style={{ height: `${fill * 100}%`, transition: 'height 0.1s linear' }}
      />
    </div>
    <button onClick={onClick} className="text-left">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[0.75rem] font-semibold transition-colors ${
            isActive ? 'border-accent bg-accent/10 text-accent' : 'border-white/20 text-white/50'
          }`}
        >
          {step.n}
        </span>
        <span
          className={`font-heading text-[1.05rem] font-semibold transition-colors md:text-[1.2rem] ${
            isActive ? 'text-accent' : 'text-white'
          }`}
        >
          {step.title}
        </span>
      </div>
      <p className="mt-2 pl-[2.5rem] text-[0.88rem] leading-relaxed text-white/55">{step.body}</p>
    </button>
  </div>
);

const TeacherHowItWorks = () => {
  const { ref, progress } = useScrollProgress();
  const scaled = progress * N;
  const index = Math.min(N - 1, Math.floor(scaled));

  return (
    <section ref={ref} className="relative pb-36 lg:h-[340vh]">
      {/* desktop pinned */}
      <div className="sticky top-0 hidden h-screen items-center overflow-hidden lg:flex">
        <div className="glow left-1/2 top-0 h-[420px] w-[700px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative w-full">
          <Header />
          <div className="mt-12 grid grid-cols-[1fr_1fr] items-center gap-14">
            <div className="flex flex-col gap-9">
              {STEPS.map((s, i) => {
                const fill = i < index ? 1 : i === index ? Math.min(1, Math.max(0, scaled - i)) : 0;
                return (
                  <StepRow
                    key={s.n}
                    step={s}
                    isActive={i === index}
                    fill={fill}
                    onClick={() => {
                      const el = ref.current;
                      if (!el) return;
                      const distance = el.offsetHeight - window.innerHeight;
                      window.scrollTo({
                        top: el.offsetTop + ((i + 0.5) / N) * distance,
                        behavior: 'smooth',
                      });
                    }}
                  />
                );
              })}
            </div>
            <div className="relative h-[320px]">
              {VISUALS.map((V, i) => (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-500 ease-out"
                  style={{
                    opacity: i === index ? 1 : 0,
                    transform: i === index ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.98)',
                    pointerEvents: i === index ? 'auto' : 'none',
                  }}
                >
                  <V />
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* mobile stacked */}
      <div className="lg:hidden">
        <Container className="py-16">
          <Reveal>
            <Header />
          </Reveal>
          <div className="mt-10 flex flex-col gap-10">
            {STEPS.map((s, i) => {
              const V = VISUALS[i];
              return (
                <Reveal key={s.n} delay={40}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-accent bg-accent/10 text-[0.8rem] font-semibold text-accent">
                      {s.n}
                    </span>
                    <span className="font-heading text-[1.15rem] font-semibold text-accent">
                      {s.title}
                    </span>
                  </div>
                  <p className="mt-2 pl-11 text-[0.9rem] leading-relaxed text-white/60">{s.body}</p>
                  <div className="mt-5">
                    <V />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </div>
    </section>
  );
};

export default TeacherHowItWorks;
